import { LocalizedLink } from "@/i18n/language";
import { useTranslation } from "react-i18next";
import Navbar from "@/components/feature/Navbar";
import Footer from "@/components/feature/Footer";
import PageHeader from "@/components/feature/PageHeader";
import BookingCompanyNotice from "@/components/feature/BookingCompanyNotice";
import { img } from "@/lib/images";
import { usePriceMap } from "@/hooks/usePrices";

const dayTrips = [
  {
    slug: "day-trip-1",
    img: img("activities/day-trips/day-trips-tour1-01.jpg"),
    titleKey: "daytrip_1_title",
    subtitleKey: "daytrip_1_subtitle",
    descKey: "daytrip_1_desc",
    tagKey: "daytrip_1_tag",
  },
  {
    slug: "day-trip-2",
    img: img("activities/day-trips/day-trips-tour2-01.jpg"),
    titleKey: "daytrip_2_title",
    subtitleKey: "daytrip_2_subtitle",
    descKey: "daytrip_2_desc",
    tagKey: "daytrip_2_tag",
  },
  {
    slug: "day-trip-3",
    img: img("activities/day-trips/day-trips-tour3-01.jpg"),
    titleKey: "daytrip_3_title",
    subtitleKey: "daytrip_3_subtitle",
    descKey: "daytrip_3_desc",
    tagKey: "daytrip_3_tag",
  },
  {
    slug: "day-trip-4",
    img: img("activities/day-trips/day-trips-tour4-01.jpg"),
    titleKey: "daytrip_4_title",
    subtitleKey: "daytrip_4_subtitle",
    descKey: "daytrip_4_desc",
    tagKey: "daytrip_4_tag",
  },
  {
    slug: "day-trip-5",
    img: img("activities/day-trips/day-trips-tour5-01.jpg"),
    titleKey: "daytrip_5_title",
    subtitleKey: "daytrip_5_subtitle",
    descKey: "daytrip_5_desc",
    tagKey: "daytrip_5_tag",
  },
  {
    slug: "day-trip-6",
    img: img("activities/day-trips/day-trips-tour6-01.jpg"),
    titleKey: "daytrip_6_title",
    subtitleKey: "daytrip_6_subtitle",
    descKey: "daytrip_6_desc",
    tagKey: "daytrip_6_tag",
  },
  {
    slug: "day-trip-7",
    img: img("activities/day-trips/day-trips-tour7-01.jpg"),
    titleKey: "daytrip_7_title",
    subtitleKey: "daytrip_7_subtitle",
    descKey: "daytrip_7_desc",
    tagKey: "daytrip_7_tag",
  },
];

export default function DayTripsPage() {
  const { t, i18n } = useTranslation();
  const isDe = i18n.language.startsWith("de");
  const { priceMap, money } = usePriceMap();

  return (
    <div className="bg-background-50 text-foreground-950 min-h-screen">
      <Navbar />
      <main>
        <PageHeader
          eyebrow={t("daytrips_eyebrow")}
          title={t("daytrips_title")}
          subtitle={t("daytrips_intro")}
          image={img("activities/day-trips/header-daytrips-01.jpg")}
          imageAlt="Bali day trips"
        />

        <section className="py-12 md:py-16 bg-background-50">
          <div className="max-w-7xl mx-auto px-4 md:px-8">
            <div className="text-center max-w-3xl mx-auto mb-8">
              <p className="text-foreground-700 leading-relaxed">
                {t("daytrips_desc")}
              </p>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-6 md:gap-8">
              {dayTrips.map((dt, i) => (
                <div
                  key={i}
                  className="group bg-background-50 border border-background-200 rounded-lg overflow-hidden hover:border-accent-600 transition-all"
                >
                  <div className="relative aspect-[4/3] overflow-hidden">
                    <img
                      src={dt.img}
                      alt={t(dt.titleKey)}
                      className="w-full h-full object-cover object-center group-hover:scale-105 transition-transform duration-700"
                    />
                    <div className="absolute top-4 right-4 bg-background-50/95 text-foreground-900 text-xs font-semibold px-3 py-1 rounded-full">
                      {t(dt.tagKey)}
                    </div>
                  </div>
                  <div className="p-6">
                    <h3 className="font-heading text-xl md:text-2xl text-foreground-950 mb-1">
                      {t(dt.titleKey)}
                    </h3>
                    <p className="text-sm font-medium text-accent-700 mb-3">
                      {t(dt.subtitleKey)}
                    </p>
                    <div className="w-10 h-0.5 bg-accent-500 mb-4"></div>
                    <p className="text-sm text-foreground-700 leading-relaxed">
                      {t(dt.descKey)}
                    </p>
                    <div className="mt-4 pt-4 border-t border-background-200 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
                      <span className="text-sm font-semibold text-foreground-900 whitespace-nowrap sm:order-2">
                        {isDe
                          ? `${money(priceMap["day_trip"])} / Erw. · ${money(priceMap["day_trip_child"])} / Kind`
                          : `${money(priceMap["day_trip"])} / adult · ${money(priceMap["day_trip_child"])} / child`}
                      </span>
                      <LocalizedLink
                        to={`/tour/${dt.slug}`}
                        className="inline-flex items-center gap-1.5 text-sm font-medium bg-primary-500 hover:bg-primary-600 text-background-50 px-4 py-2 rounded-md whitespace-nowrap cursor-pointer transition-colors sm:order-1"
                      >
                        {t("tour_detail_read_more")}
                        <i className="ri-arrow-right-line"></i>
                      </LocalizedLink>
                    </div>
                  </div>
                </div>
              ))}
            </div>

            <div className="mt-10 flex flex-col items-center">
              <LocalizedLink
                to="/booking"
                className="inline-flex items-center gap-2 bg-primary-500 hover:bg-primary-600 text-background-50 px-7 py-3 rounded-md font-medium whitespace-nowrap cursor-pointer transition-colors"
              >
                <i className="ri-calendar-check-line"></i>
                {t("daytrips_book_tour")}
              </LocalizedLink>
              <div className="max-w-2xl">
                <BookingCompanyNotice isDe={isDe} />
              </div>
            </div>
          </div>
        </section>

        <section className="py-12 md:py-14 bg-background-50">
          <div className="max-w-7xl mx-auto px-4 md:px-8">
            <div className="flex flex-col md:flex-row items-center justify-between gap-6">
              <div>
                <h2 className="font-heading text-2xl md:text-3xl text-foreground-950">
                  {t("daytrips_cta_title")}
                </h2>
                <p className="mt-2 text-foreground-700 text-sm">
                  {t("daytrips_cta_sub")}
                </p>
              </div>
              <div className="flex flex-col sm:flex-row items-center gap-3">
                <LocalizedLink
                  to="/contact"
                  className="inline-flex items-center gap-2 bg-primary-500 hover:bg-primary-600 text-background-50 px-7 py-3.5 rounded-md font-medium whitespace-nowrap cursor-pointer transition-colors"
                >
                  <i className="ri-mail-send-line"></i>
                  {t("daytrips_cta_contact")}
                </LocalizedLink>
                <a
                  href="https://wa.me/6281246218000"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center gap-2 bg-[#1DA851] hover:bg-[#15823F] text-white px-7 py-3.5 rounded-md font-medium whitespace-nowrap cursor-pointer transition-colors"
                >
                  <i className="ri-whatsapp-line text-lg"></i>
                  WhatsApp
                </a>
              </div>
            </div>
          </div>
        </section>
      </main>
      <Footer />
    </div>
  );
}